import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

import { logger } from '../utils/logger';
/**
 * ErrorContext - Global Error State Management
 * 
 * Manages application-level errors with comprehensive features:
 * - Error queue management (multiple errors)
 * - Automatic error dismissal (configurable timeout)
 * - Error reporting integration (future: Sentry)
 * - Retry mechanisms
 * - Error categorization (api, network, auth, validation)
 * 
 * Industry Pattern: Sentry context, Bugsnag error handling
 * 
 * @context
 * @example
 * ```tsx
 * // In a component
 * const { addError, clearError } = useError();
 * 
 * try {
 *   await apiCall();
 * } catch (error) {
 *   addError({
 *     type: 'api',
 *     message: error.message,
 *     retry: () => apiCall(),
 *     dismissible: true
 *   });
 * }
 * ```
 */

export interface AppError {
  /** Unique error ID */
  id: string;
  /** Error type for categorization */
  type: 'api' | 'network' | 'auth' | 'validation' | 'unknown';
  /** Error message for users */
  message: string;
  /** Optional error details (for developers) */
  details?: any;
  /** Retry function (if action is retryable) */
  retry?: () => void | Promise<void>;
  /** Can be dismissed by user */
  dismissible?: boolean;
  /** Auto-dismiss after N milliseconds */
  autoDismiss?: number;
  /** Timestamp when error occurred */
  timestamp: Date;
}

interface ErrorContextValue {
  /** Array of active errors */
  errors: AppError[];
  /** Add a new error */
  addError: (error: Omit<AppError, 'id' | 'timestamp'>) => string;
  /** Clear a specific error by ID */
  clearError: (id: string) => void;
  /** Clear all errors */
  clearAllErrors: () => void;
  /** Get errors by type */
  getErrorsByType: (type: AppError['type']) => AppError[];
}

const ErrorContext = createContext<ErrorContextValue | undefined>(undefined);

export interface ErrorProviderProps {
  /** Child components */
  children: ReactNode;
}

/**
 * ErrorProvider Component
 * 
 * Wraps the application to provide global error state.
 * Should be placed high in the component tree.
 * 
 * @param {ErrorProviderProps} props - Component props
 * @returns {JSX.Element} Provider component
 */
export const ErrorProvider: React.FC<ErrorProviderProps> = ({ children }) => {
  const [errors, setErrors] = useState<AppError[]>([]);

  /**
   * Clear a specific error by ID
   */
  const clearError = useCallback((id: string) => {
    setErrors(prev => prev.filter(error => error.id !== id));
  }, []);

  /**
   * Add a new error to the queue
   * Automatically generates ID and timestamp
   * Sets up auto-dismiss if configured
   */
  const addError = useCallback((errorData: Omit<AppError, 'id' | 'timestamp'>): string => {
    const id = `error-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newError: AppError = {
      ...errorData,
      id,
      timestamp: new Date(),
    };

    setErrors(prev => [...prev, newError]);

    // Auto-dismiss if configured
    if (errorData.autoDismiss) {
      setTimeout(() => {
        clearError(id);
      }, errorData.autoDismiss);
    }

    // Future: Send to error reporting service (Sentry, Bugsnag, etc.)
    if (process.env.NODE_ENV === 'development') {
      logger.error('[ErrorContext] New error:', { data: newError });
    }

    return id;
  }, [clearError]);

  /**
   * Clear all errors
   */
  const clearAllErrors = useCallback(() => {
    setErrors([]);
  }, []);

  /**
   * Get errors filtered by type
   */
  const getErrorsByType = useCallback((type: AppError['type']): AppError[] => {
    return errors.filter(error => error.type === type);
  }, [errors]);

  const value: ErrorContextValue = {
    errors,
    addError,
    clearError,
    clearAllErrors,
    getErrorsByType,
  };

  return (
    <ErrorContext.Provider value={value}>
      {children}
    </ErrorContext.Provider>
  );
};

/**
 * useError Hook
 * 
 * Access global error state and methods.
 * 
 * @throws {Error} If used outside ErrorProvider
 * @returns {ErrorContextValue} Error context value
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { addError, errors, clearError } = useError();
 *   
 *   const handleSubmit = async () => {
 *     try {
 *       await saveData();
 *     } catch (err) {
 *       addError({
 *         type: 'api',
 *         message: 'Failed to save data',
 *         retry: handleSubmit,
 *         dismissible: true,
 *         autoDismiss: 5000
 *       });
 *     }
 *   };
 *   
 *   return (
 *     <div>
 *       {errors.map(error => (
 *         <ErrorBanner 
 *           key={error.id} 
 *           error={error} 
 *           onDismiss={() => clearError(error.id)}
 *         />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export function useError(): ErrorContextValue {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within ErrorProvider');
  }
  return context;
}

